import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Col, Input, Label, Row } from 'reactstrap';

import styles from './style.module.scss';

import { BoxType, GetPlanId } from '../../../helpers/Helpers';

import SectionLoader from '../../../components/Common/loader/SectionLoader';
import NoDataFound from '../../../components/Common/no-data-found/no-data-found';
import BarChart from '../../../components/Common/Charts/BarChart';

import { IInventoryAnalysisChart } from '../../../interfaces/charts.interface';
import { IMeasureVisibilityPayload } from '../../../interfaces/common.interface';
import { GetInventoryAnalysisChartData } from '../../../api-services/rolling-plan-charts.api';
import { UpdateMeasureVisibility } from '../../../api-services/common.api';

interface OwnProps {
  isCallRequired: boolean;
  onLoadEnd: () => void;
}

interface StateProps {
  history: any;
}

interface DispatchProps {
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  isLoading: boolean;
  originalDataSource: IInventoryAnalysisChart[];
  dataSource: IInventoryAnalysisChart[];
  periodList: string[];
  selectedPeriod: string;
  isModalOpened: boolean;
}

class InventoryAnalysis extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      originalDataSource: [],
      dataSource: [],
      periodList: ['QUARTER', 'WEEK'],
      selectedPeriod: 'WEEK',
      isModalOpened: false,
    };
  }

  componentDidMount(): void {
    this.getInventoryAnalysisChartData();
  }

  componentDidUpdate(prevProps, prevState): void {
    if (!prevProps.isCallRequired && this.props.isCallRequired) {
      this.getInventoryAnalysisChartData();
    }
  }

  getInventoryAnalysisChartData(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.setState({ isLoading: true });

    GetInventoryAnalysisChartData(this.state.selectedPeriod.toLocaleLowerCase(), params)
      .then((res: IInventoryAnalysisChart[]) => {
        this.setState({
          originalDataSource: !!res ? res : []
        }, () => {
          this.dataInit();
          this.props.onLoadEnd();
        });
      });
  }

  dataInit(): void {
    const data: IInventoryAnalysisChart[] = [...this.state.originalDataSource];
    const dataSource: IInventoryAnalysisChart[] = data.map((group) => {
      return {
        ...group,
        checked: this.state.originalDataSource.length === 1 ? true : (group.name === 'REVENUE'),
        expanded: true,
        disabled: group.name === 'REVENUE',
        children: group.children.map((subgroup) => {
          return {
            ...subgroup,
            values: subgroup.values.map((value) => {
              return {
                ...value,
                checked: value.checked,
                prevChecked: value.checked,
              };
            })
          };
        })
      };
    });

    this.setState({
      originalDataSource: dataSource,
      isLoading: false
    }, () => {
      this.updateDataSource();
    });
  }

  onUpdateGroup(groupIdx: number): void {
    this.state.originalDataSource[groupIdx].checked = !this.state.originalDataSource[groupIdx].checked;

    const checkCount = this.state.originalDataSource.reduce((acc, group) => {
      acc += group.checked ? 1 : 0;
      return acc;
    }, 0);

    if (checkCount === 1) {
      const checkedIndex = this.state.originalDataSource.findIndex((group) => group.checked);
      this.state.originalDataSource[checkedIndex].disabled = true;
    } else {
      const disabledIndex = this.state.originalDataSource.findIndex((group) => group.disabled);
      if (disabledIndex > -1) {
        this.state.originalDataSource[disabledIndex].disabled = false;
      }
    }

    this.updateDataSource();
  }

  onUpdateSubgroup(groupIdx: number, valueIdx: number): void {
    this.state.originalDataSource[groupIdx].children.forEach((subGroup) => {
      subGroup.values[valueIdx].checked = !subGroup.values[valueIdx].checked;
    });

    this.updateDataSource();
  }

  updateDataSource(): void {
    const filteredDataSource = this.state.originalDataSource
      .filter((group) => group.checked && group.children
        .map((subgroup) => subgroup.values
          .some((value) => value.checked)))
      .map((group) => Object.assign({}, group, {
        children: group.children
          .map((subgroup) => Object.assign({}, subgroup, {
            values: subgroup.values
              .filter((value) => value.checked)
          }))
      }));

    this.setState({ dataSource: filteredDataSource });
  }

  toggleExpandLegend(groupIdx: number): void {
    this.state.originalDataSource[groupIdx].expanded = !this.state.originalDataSource[groupIdx].expanded;
    this.setState({ originalDataSource: this.state.originalDataSource });
  }

  togglePeriod(period: string): void {
    this.setState({
      selectedPeriod: period
    }, () => {
      this.getInventoryAnalysisChartData();
    });
  }

  updateMeasureVisibility(): void {
    const payload: IMeasureVisibilityPayload = {
      visibility: this.state.originalDataSource
        .flatMap(item => item.children
          .flatMap(child => child.values
            .map(measure => ({
              name: measure.name,
              isVisible: measure.checked,
            }))
          )
        )
    };
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname),
      chart: 'inventoryAnalysis',
    };

    UpdateMeasureVisibility(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.getInventoryAnalysisChartData();
          });
        }
      });
  }

  checkVisibilitySaveButton(): boolean {
    return this.state.originalDataSource
      .every(item => item.children
        .every(child => child.values
          .every(measure => measure.checked === measure.prevChecked)));
  }

  render(): React.ReactNode {
    if (this.state.isLoading) {
      return <SectionLoader/>;
    }

    return (
      <React.Fragment>
        <Row style={{ marginTop: '18px' }}>
          <Col md={9} className="text-center">

            <Row className="align-items-center">
              <Col md={{ offset: 3, size: 3 }}>

                <div className="d-flex">
                  {
                    this.state.periodList.map((period: string, idx: number) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center"
                      >
                        <Input
                          id={period}
                          type="radio"
                          checked={period === this.state.selectedPeriod}
                          onChange={() => this.togglePeriod(period)}
                          className="me-1"
                        />
                        <Label
                          for={period}
                          className={styles['measure-group']}
                        >
                          {period}
                        </Label>
                      </div>
                    ))
                  }
                </div>

              </Col>

              <Col md={4}>
                <div className="d-flex">
                  {
                    this.state.originalDataSource.map((group, idx: number) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center"
                      >
                        <Input
                          id={group.name}
                          type="checkbox"
                          checked={group.checked}
                          disabled={group.disabled}
                          onChange={() => this.onUpdateGroup(idx)}
                          className="me-1"
                        />
                        <Label for={group.name} className={styles['measure-group']}>{group.name}</Label>
                      </div>
                    ))
                  }
                </div>
              </Col>

              <Col md={1}>
                <Button
                  color={'primary'}
                  className='btn btn-primary btn-sm'
                  disabled={this.checkVisibilitySaveButton()}
                  onClick={() => this.updateMeasureVisibility()}
                >
                  Save
                </Button>
              </Col>
            </Row>

            {
              (!this.state.isLoading && this.state.dataSource.length === 0)
                ? <NoDataFound/>
                : <BarChart
                  dataSource={this.state.dataSource}
                  component={'InventoryAnalysis'}
                  period={this.state.selectedPeriod}
                />
            }

          </Col>

          <Col md={3}>

            <div className={styles['legend-section']}>
              {
                this.state.originalDataSource.map((group, groupIdx: number) => (
                  <React.Fragment key={groupIdx}>
                    {
                      group.checked &&
                      <div
                        className={styles['legend-container']}
                        onClick={() => this.toggleExpandLegend(groupIdx)}
                      >
                        <span className={styles['legend-header']}>
                          {group.name} Measure
                        </span>
                        <div className={`${styles['box']} ${styles[BoxType(group.name)]}`}/>
                        <i
                          className={`ri-arrow-${group.expanded ? 'up' : 'down'}-s-line`}
                          style={{ fontSize: '22px' }}
                        />
                      </div>
                    }
                    {
                      (group.checked && group.expanded) &&
                      group.children[0].values.map((value, valueIdx: number) => (
                        <React.Fragment key={valueIdx}>
                          <div className="d-flex align-items-center my-2">
                            <Input
                              id={value.name}
                              type="checkbox"
                              checked={value.checked}
                              onChange={() => this.onUpdateSubgroup(groupIdx, valueIdx)}
                            />

                            <div
                              className={styles['legend-color-box']}
                              style={{ backgroundColor: value.color }}
                            />

                            <Label
                              for={value.name}
                              className={styles['line-chart-legend-label']}
                            >
                              {value.name}
                            </Label>
                          </div>
                        </React.Fragment>
                      ))
                    }
                  </React.Fragment>
                ))
              }
            </div>

          </Col>
        </Row>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any): StateProps => {
  return state;
};

export default connect<StateProps, DispatchProps, OwnProps>
(mapStateToProps, {})(withRouter(InventoryAnalysis));
